import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ContextShell } from '../../modules/context'
import { Announcement, useAnnouncements } from './use-announcements'
import { useStoreModals } from '../modals/use-store-modals'

export type UseAnnouncement = {
  title: string | null
  description: string | null
  image: string | null
  name: string | null
  buttonText: string | null
  buttonUrl: string | null
  tagLabel: string | null
  show: boolean
  onCloseBanner: (e: any) => void
  onClickBanner: () => void
  startAt: string | null
  showNewFeature: (url: string) => boolean
  onClickNewFeature: (url: string) => void
}

export const useAnnouncement = (): UseAnnouncement => {
  const [show, setShow] = useState(false)
  const [haveAnnouncement, setHaveAnnouncement] = useState(false)
  const [announcement, setAnnouncements] = useState<Announcement | undefined>(
    undefined
  )

  const { value, loadAnnouncements, dismissAnnouncement } = useAnnouncements()
  const {
    user,
    event: { sendEvent }
  } = useContext(ContextShell)
  const {
    modalGroupPlanTrial: { onOpen: onOpenModalGroupPlanTrial }
  } = useStoreModals()

  useEffect(() => {
    if (!value) {
      setAnnouncements(undefined)
      setHaveAnnouncement(false)
      return
    }

    const url = value?.button?.url
    if (
      url === 'type:announcement-group-plan-promotion' &&
      !user?.subscription?.eligibleGroupPlanTrial
    ) {
      setAnnouncements(undefined)
      setHaveAnnouncement(false)
      return
    }

    setAnnouncements(value)
    setHaveAnnouncement(true)
  }, [value, user?.subscription?.eligibleGroupPlanTrial])

  useEffect(() => {
    if (haveAnnouncement) {
      setShow(true)
    } else {
      setShow(false)
    }
  }, [haveAnnouncement])

  useEffect(() => {
    if (user?.id) {
      if (!announcement?.id) {
        loadAnnouncements()
      }
    }
  }, [announcement, loadAnnouncements, user?.id])

  const title = useMemo(() => announcement?.title || '', [announcement])

  const description = useMemo(
    () => announcement?.description || '',
    [announcement]
  )

  const image = useMemo(() => announcement?.image || '', [announcement])

  const name = useMemo(() => announcement?.name || '', [announcement])

  const tagLabel = useMemo(() => announcement?.tagLabel || null, [announcement])

  const buttonText = useMemo(
    () => announcement?.button?.label || '',
    [announcement]
  )

  const buttonUrl = useMemo(
    () => announcement?.button?.url || '',
    [announcement]
  )

  const startAt = useMemo(() => announcement?.startAt || '', [announcement])

  const onDismiss = useCallback(() => {
    setShow(false)
    dismissAnnouncement({
      id: announcement?.id || '',
      elements: ['SIDEBANNER']
    })
  }, [dismissAnnouncement, announcement?.id])

  const onCloseBanner = useCallback(
    (e: any) => {
      e?.stopPropagation()
      onDismiss()
    },
    [onDismiss]
  )

  const onClickBanner = useCallback(() => {
    onDismiss()
    sendEvent({
      name: 'announcement_clicked',
      category: 'engagement',
      customAttributes: {
        announcement_name: name || 'announcement'
      }
    })

    const url = announcement?.button?.url

    if (url === 'type:announcement-group-plan-promotion') {
      onOpenModalGroupPlanTrial()
    } else {
      window.open(
        announcement?.button?.url || '/',
        announcement?.button?.newTab ? '_blank' : '_self'
      )
    }
  }, [name, announcement, onDismiss, sendEvent, onOpenModalGroupPlanTrial])

  const isUrlCorrect = useCallback(
    (url: string) => !!announcement?.button?.url?.includes(url),
    [announcement?.button?.url]
  )

  const showNewFeature = useCallback(
    (url: string) => (haveAnnouncement ? isUrlCorrect(url) : false),
    [isUrlCorrect, haveAnnouncement]
  )

  const onClickNewFeature = useCallback(
    (url: string) => {
      if (haveAnnouncement && isUrlCorrect(url)) {
        onDismiss()
      }
    },
    [haveAnnouncement, isUrlCorrect, onDismiss]
  )

  return {
    title,
    description,
    image,
    buttonText,
    tagLabel,
    show: show && haveAnnouncement,
    onCloseBanner,
    onClickBanner,
    startAt,
    name,
    showNewFeature,
    onClickNewFeature,
    buttonUrl
  }
}
